import {useErrorHandler} from "~/components/ErrorHandling";
import {useQuery} from "@apollo/client";
import {
    getAutoShipmentOrdersPathBuilder,
    GetAutoShipmentOrdersQueryResult,
    GetAutoShipmentOrdersQueryVariables,
    GetAutoShipmentOrdersDocument,
    OrderItem,
    GetAutoShipmentOrderDetailDocument,
    GetAutoShipmentOrderQueryResult,
    GetAutoShipmentOrderQueryVariables,
    Cargo,
    GetCargoListVars,
    GetCargoListDocument,
    GetCargoListPathBuilder,
    GetCargoListResult,
    GetContractListDocument,
    GetContractListQueryResult,
    GetContractListQueryVars, GetCarriersListDocument, GetCarriersListQueryResult, GetCarriersListQueryVars
} from "~/apps/packages/auto-shipment-orders/queries";
import {useCallback, useEffect, useMemo} from "react";
import moment from "moment/moment";

export type UseAutoShipmentOrdersListOptions = {
    query?: string
    dateFrom?: Date
    dateTo?: Date
    status?: OrderItem["status"][]
}
export const useAutoShipmentOrdersList = (options: UseAutoShipmentOrdersListOptions) => {
    const handleError = useErrorHandler();
    const { data, error, refetch, fetchMore: _fetchMore, loading } = useQuery<GetAutoShipmentOrdersQueryResult, GetAutoShipmentOrdersQueryVariables>(GetAutoShipmentOrdersDocument, {
        notifyOnNetworkStatusChange: true,
        variables: {
            status: options.status,
            query: options.query,
            dateFrom: options?.dateFrom?.getTime(),
            dateTo: options?.dateTo?.getTime(),
            pathBuilder: getAutoShipmentOrdersPathBuilder,
        }
    });

    const canFetchMore = Boolean(data?.autoShipmentOrders?.pageInfo?.nextPage) && !loading;

    const fetchMore = useCallback(() => {
        if (!canFetchMore) {
            return;
        }
        _fetchMore({
            variables: {
                page: data?.autoShipmentOrders?.pageInfo.nextPage
            },
        }).catch(handleError);
    }, [data, canFetchMore]);

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutId = setTimeout(() => refetch(), 5000);
            return () => clearTimeout(timeoutId);
        }
    }, [error])

    return {
        data: data?.autoShipmentOrders?.autoShipmentOrders,
        loading: loading || error != null,
        fetchMore,
        canFetchMore
    }
}

export const useAutoShipmentOrder = (id: string) => {
    const handleError = useErrorHandler();
    const {
        data: rawData,
        loading,
        error,
        refetch,
    } = useQuery<GetAutoShipmentOrderQueryResult, GetAutoShipmentOrderQueryVariables>(GetAutoShipmentOrderDetailDocument, {
        variables: {
            id: id
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "cache-and-network",
    })

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutId = setTimeout(() => refetch(), 5000);
            return () => clearTimeout(timeoutId);
        }
    }, [error]);

    const data = useMemo(() => {
        if (!rawData?.autoShipmentOrder?.autoShipmentOrder) {
            return undefined;
        }
        return rawData?.autoShipmentOrder?.autoShipmentOrder;
    }, [rawData]);

    console.log('data', rawData, data)

    return {
        data: data,
        loading: loading || error != null
    }
}

export type useCargoListOptions = {
    autoVisitId?: string
    date?: Date
    excludeCargoIds?: string[]
    page?: number
    type?: ("Генеральный груз" | "Насыпной груз" | "КТК")[],
    query?: string
}
export const useCargoList = (options: useCargoListOptions) => {
    const { page, query, type, excludeCargoIds} = options;
    const handleError = useErrorHandler();
    const typeMapping: {[key: string]: Cargo["type"]} = {
        "Генеральный груз": "Генеральный груз",
        "Насыпной груз": "Насыпной груз",
        "КТК": "КТК",
    };
    const empty = useMemo(() => {
       /* if (!type) {
            return null;
        } else if (type.includes("КТК груженый") && type.includes("КТК порожний")) {
            return null;
        } else if (type.includes("КТК груженый")) {
            return false;
        } else if (type.includes("КТК порожний")) {
            return true;
        }*/
    }, [type]);

    const {
        data,
        loading,
        error,
        refetch,
        fetchMore: _fetchMore
    } = useQuery<GetCargoListResult, GetCargoListVars>(GetCargoListDocument, {
        notifyOnNetworkStatusChange: true,
        variables: {
            excludeIds: excludeCargoIds,
            //date: date ? moment(date).toDate().getTime() : null,
            page: page,
            query: query,
            //empty: empty,
            type: [...new Set(type?.map(item => typeMapping[item]))],
            pathBuilder: GetCargoListPathBuilder
        }
    });

    const canFetchMore = Boolean(data?.cargos?.pageInfo?.nextPage) && !loading;
    const fetchMore = useCallback(() => {
        if (!canFetchMore) {
            return;
        }
        _fetchMore({
            variables: {
                page: data.cargos.pageInfo.nextPage
            },
        }).catch(handleError);
    }, [data, canFetchMore]);

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutId = setTimeout(() => refetch(), 5000);
            return () => clearTimeout(timeoutId);
        }
    }, [error]);

    return {
        data: data?.cargos?.cargos,
        loading: loading || error != null,
        fetchMore,
        canFetchMore
    }
}

export function useContractList() {
    const handleError = useErrorHandler();
    const {data, loading, error, refetch} =  useQuery<GetContractListQueryResult, GetContractListQueryVars>(GetContractListDocument, {
        notifyOnNetworkStatusChange: true
    });

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutID = setTimeout(refetch, 5000);
            return () => clearTimeout(timeoutID);
        }
    }, [error]);

    return {
        data: data?.autoShipmentOrderContracts?.contracts,
        loading: loading || error != null
    }
}

export function useCarrierList() {
    const handleError = useErrorHandler();
    const {data, loading, error, refetch} =  useQuery<GetCarriersListQueryResult, GetCarriersListQueryVars>(GetCarriersListDocument, {
        notifyOnNetworkStatusChange: true
    });

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutID = setTimeout(refetch, 5000);
            return () => clearTimeout(timeoutID);
        }
    }, [error]);

    return {
        data: data?.autoShipmentOrderCarriers,
        loading: loading || error != null
    }
}